import * as React from 'react';
import strings from '../../../../strings/strings.json';
import { logError } from '../../../../lib/debug-helpers';
import { Course } from '../../../../models/Course';
import { APIError } from '../../../../lib/_api-helpers';
import { ErrorMessage, FormCard } from '../../../../components/forms/FormComponents';
import {
    createGlobalErrorFromAPIError,
    createFieldErrorFromAPIError,
    requiredFieldErrorMessage,
    createErrorMessage,
    fieldErrorCodes,
} from '../../../../shared/error-messages';
import TextField from '../../../../components/forms/TextField';
import useModifyRequest from '../../../../hooks/useModifyRequest';
import useNeveForm from 'components/forms/NeveForm';
import TextAreaField from 'components/forms/TextAreaField';
import AccessibilityNotifier from '../../../../components/notifiers/AccessibilityNotifier';
import { capitalsRegex } from '../../../../components/accessibilityGuide/utils';
import { styled } from 'styled-components';
import useFormBlocker from 'components/forms/useFormBlocker';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import NavButton from 'components/buttons/NavButton';
import useWindowWidth from 'hooks/useWindowWidth';
import {
    SaveButtonAndNotificationContainer,
    RightNavButtonContainer,
} from 'pages/build-course/shared-styles/courseBuilderFormComponents';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { EditModeSaveNotification } from 'pages/build-course/components/activityForms/editActivityForms/shared/SharedEditTaskComponents';
import Button from 'components/buttons/Button';

export function CourseDetailsForm(): JSX.Element {
    const { setCourseState, editMode, refetchCourse, setActiveStep } = React.useContext(BuildCourseContext);
    const course = React.useContext(BuildCourseContext).course!;

    const [globalErrorMessage, setGlobalErrorMessage] = React.useState<string>('');
    const [saved, setSaved] = React.useState<boolean>(false);

    const { isMobileWidth } = useWindowWidth();
    const { modifyData: updateCourse, loading: saving } = useModifyRequest(
        `courses/${course!.id}/title-and-description`,
        'PUT',
    );

    const saveButtonRef = React.useRef<HTMLButtonElement>(null);

    const {
        register,
        handleSubmit,
        setError,
        watch,
        formState: { errors, isDirty },
        reset,
    } = useNeveForm<CourseDetailsInput>({
        title: course.title,
        description: course.description,
    });

    React.useEffect(() => {
        reset({
            title: course.title,
            description: course.description,
        });
    }, [course.title, course.description]); //eslint-disable-line

    React.useEffect(() => {
        if (isDirty && saved) {
            setSaved(false);
        }
    }, [isDirty, saved]);

    useFormBlocker(isDirty, strings.courseEditorPage.unsavedChangesPrompt);

    async function onSubmit(formData: CourseDetailsInput) {
        if (!formData.title || !formData.description) return;
        setSaved(false);

        if (editMode) {
            setCourseState((prev) => ({
                ...prev!,
                title: formData.title,
                description: formData.description,
            }));
        } else {
            const newCourse = {
                ...course!,
                title: formData.title,
                description: formData.description,
            };

            const { errors } = await updateCourse(newCourse as Course);
            if (errors && errors.length) {
                logError(errors);
                updateErrors(errors);
                return;
            } else {
                setGlobalErrorMessage('');
                await refetchCourse();
            }
        }

        reset(formData);
        setSaved(true);
        saveButtonRef.current?.focus();
    }

    function updateErrors(apiErrors: APIError[]): void {
        apiErrors.forEach((apiError) => {
            const { field, code } = apiError;
            if (field === 'global') {
                return setGlobalErrorMessage(createGlobalErrorFromAPIError(code));
            }
            setError(field as keyof CourseDetailsInput, createFieldErrorFromAPIError(field, code));
        });
    }

    const courseTitle = watch('title');
    const courseDescription = watch('description');

    React.useEffect(() => {
        setSaved(false);
    }, [courseTitle, courseDescription]);

    return (
        <>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <TextField
                        maxLength={100}
                        fieldName="courseTitle"
                        labelText={strings.courseDetailsForm.courseTitleInputLabel}
                        inputAria={strings.courseDetailsForm.courseTitleInputLabelAria}
                        errorMessage={errors.title?.message}
                        required
                        autoFocus
                        {...register('title', {
                            required: {
                                value: true,
                                message: requiredFieldErrorMessage(strings.courseDetailsForm.courseTitleInputLabel),
                            },
                        })}
                    />
                    {capitalsRegex.test(courseTitle) && (
                        <AccessibilityNotifier accessibilityGuideKey={'textAllCaps'} />
                    )}
                    <TextAreaField
                        maxLength={1000}
                        charactersLeft={1000 - courseDescription.length}
                        fieldName={strings.courseDetailsForm.courseDescriptionInputLabel}
                        aria={strings.courseDetailsForm.courseDescriptionInputLabelAria}
                        errorMessage={errors.description?.message}
                        required
                        {...register('description', {
                            required: {
                                value: true,
                                message: requiredFieldErrorMessage(
                                    strings.courseDetailsForm.courseDescriptionInputLabel,
                                ),
                            },
                            maxLength: {
                                value: 1000,
                                message: createErrorMessage(
                                    strings.courseDetailsForm.courseDescriptionInputLabel,
                                    fieldErrorCodes.maxLength,
                                ),
                            },
                        })}
                    />
                    {capitalsRegex.test(courseDescription) && (
                        <AccessibilityNotifier accessibilityGuideKey={'textAllCaps'} />
                    )}
                </div>

                {globalErrorMessage && <ErrorMessage id="errorMessage">{globalErrorMessage}</ErrorMessage>}

                <>
                    <SaveButtonAndNotificationContainer>
                        {saved &&
                            (editMode ? (
                                <EditModeSaveNotification />
                            ) : (
                                <Notification
                                    notificationType={NotificationType.SUCCESS}
                                    title={strings.general.saveSuccess}
                                />
                            ))}
                        <Button
                            ref={saveButtonRef}
                            type="submit"
                            title={strings.courseDetailsForm.submitButton}
                            aria={strings.courseDetailsForm.submitButtonAria}
                            disabled={editMode && !isDirty}
                            submitting={saving}
                        />
                    </SaveButtonAndNotificationContainer>

                    {!isMobileWidth && (
                        <RightNavButtonContainer>
                            <NavButton
                                title={strings.courseDetailsForm.navButton.title}
                                aria={strings.courseDetailsForm.navButton.aria}
                                onClick={() => {
                                    setActiveStep('courseLearningObjectives');
                                }}
                            />
                        </RightNavButtonContainer>
                    )}
                </>
            </Form>
        </>
    );
}

const Form = styled(FormCard)`
    width: 100%;
`;

type CourseDetailsInput = {
    title: string;
    description: string;
};
